import React, { useMemo, useState } from 'react';

import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Dialog } from '@buildoutinc/blueprint-react/ui/Dialog';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import { Label } from '@buildoutinc/blueprint-react/ui/Label';
import { Switch } from '@buildoutinc/blueprint-react/ui/Switch';

import { Button } from 'components/ui/button';
import { showSnackbar } from 'components/ui/snackbar/helper';
import { post, put } from 'helpers/api';

import { blockedBy, changedFrom, grantedCount, isLocked, rowsOf, valuesFor, withValue } from './state';

import type { DependencyRule } from 'components/users/permissions/permission_dependencies';
import type { RoleValues } from './state';
import type { CatalogGroup, ImpactPreview, RolePayload, RoleSummary } from './types';

// The impact the server reported, kept with the exact payload it was reported for.
interface PendingSave {
  impact: ImpactPreview;
  payload: RolePayload;
}

interface Props {
  role: RoleSummary;
  groups: CatalogGroup[];
  dependencyRules: DependencyRule[];
  rolesPath: string;
  onSaved: (role: RoleSummary) => void;
  onCancel: () => void;
}

const FIELD_STYLE = { maxWidth: '22rem' };

const holderSentence = (count: number): string => (
  count === 0
    ? 'No one holds this role yet.'
    : `${count} ${count === 1 ? 'person holds' : 'people hold'} this role.`
);

// The sentence the confirm dialog leads with. The count is the whole point of the dialog, so it is stated
// before anything else and never rounded or softened.
const impactSentence = ({ affectedCount }: ImpactPreview): string => (
  `${affectedCount} ${affectedCount === 1 ? 'person' : 'people'} will have their permissions change when `
  + 'this is saved.'
);

export default function RoleEditor({
  role, groups, dependencyRules, rolesPath, onSaved, onCancel,
}: Props) {
  const rows = useMemo(() => rowsOf(groups), [groups]);

  const [name, setName] = useState(role.name);
  const [values, setValues] = useState<RoleValues>(() => valuesFor(rows, role.granted));
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<PendingSave | null>(null);

  const rolePath = `${rolesPath}/${role.id}`;
  const dirty = name !== role.name || changedFrom(values, role.granted);

  // Every permission on the page with its value, not only the ones switched on. Rails drops an empty hash
  // from params, so a role stripped of every default has to travel as explicit falses or the save would
  // arrive looking like a rename — see Companies::RolesController#submitted_attributes.
  const payload = (): RolePayload => ({ role: { name, permissionDefaults: values } });

  // Editing is frozen from the moment a save starts until it resolves, the confirmation included. Without
  // it the form stays live under the dialog, and a toggle moved there would be silently dropped in favour
  // of the payload the count was computed for.
  const frozen = saving || pending !== null;

  function reportError(error: unknown) {
    const messages = Array.isArray(error) ? error : [];
    const text = messages.length > 0 ? messages.join(' ') : 'Something went wrong saving this role.';

    showSnackbar({ text, variant: 'danger' });
  }

  // Takes the payload rather than rebuilding it, so what is written is exactly what the count described.
  async function commit(committed: RolePayload) {
    setSaving(true);

    try {
      const response = await put(rolePath, committed);
      onSaved(response.role);
      showSnackbar({ text: 'This role was saved.', variant: 'success' });
    } catch (error) {
      reportError(error);
    } finally {
      setSaving(false);
      setPending(null);
    }
  }

  // Asks the server what the save would do before committing it. Held back rather than folded into the
  // save because the count is the thing the admin is meant to see first, and because nobody should have to
  // undo a role edit to find out who it moved.
  async function handleSave() {
    // Snapshotted before the request, and committed unchanged afterwards. The count the server answers with
    // describes THIS payload, so rebuilding it at commit time would let a late edit be saved under a count
    // computed for something else.
    const snapshot = payload();

    setSaving(true);

    try {
      const impact: ImpactPreview = await post(`${rolePath}/preview`, snapshot);

      if (impact.affectedCount === 0) {
        await commit(snapshot);
        return;
      }

      setPending({ impact, payload: snapshot });
    } catch (error) {
      reportError(error);
    } finally {
      setSaving(false);
    }
  }

  function toggle(key: string, value: boolean) {
    setValues(current => withValue(dependencyRules, current, key, value));
  }

  return (
    <div>
      <div className="d-flex align-items-start justify-content-between gap-3 mb-4">
        <div style={FIELD_STYLE}>
          <Label htmlFor="role-name">Role Name</Label>
          <Input
            disabled={frozen}
            id="role-name"
            type="text"
            value={name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
          />
          <p className="mb-0 mt-2 text-muted fs-small" data-testid="role-holders">
            {holderSentence(role.assignedUserCount)}
          </p>
        </div>

        <div className="d-flex gap-2">
          <Button data-testid="save-role" disabled={saving || !dirty} onClick={handleSave}>
            Save Role
          </Button>
          <Button data-testid="cancel-role" disabled={saving} variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>

      <p className="text-muted fs-small" data-testid="role-summary">
        {`${grantedCount(values)} of ${rows.length} turned on by default`}
      </p>

      {groups.map(group => (
        <Card className="mb-4" key={group.key}>
          <Card.Header>
            <Card.Title className="fw-semibold fs-large">{group.title}</Card.Title>
          </Card.Header>
          <Card.Body>
            <ul className="list-unstyled mb-0">
              {group.rows.map((row) => {
                const locked = isLocked(dependencyRules, values, row.key);
                const missing = blockedBy(rows, values, row.key);

                return (
                  <li
                    className="d-flex align-items-start justify-content-between gap-3 py-3 border-bottom"
                    key={row.key}
                  >
                    <div className="flex-grow-1">
                      <span className="fw-semibold">{row.label}</span>
                      {row.description && <p className="mb-0 text-muted fs-small">{row.description}</p>}
                      {missing.length > 0 && (
                        <p className="mb-0 fs-small text-warning-emphasis">
                          {`Cannot be turned on until ${missing.join(' and ')} is turned on.`}
                        </p>
                      )}
                    </div>
                    <Switch
                      aria-label={row.label}
                      checked={values[row.key]}
                      disabled={locked || frozen}
                      onCheckedChange={(value: boolean) => toggle(row.key, value)}
                    />
                  </li>
                );
              })}
            </ul>
          </Card.Body>
        </Card>
      ))}

      <Dialog open={pending !== null} onClose={() => setPending(null)}>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Confirm Role Changes</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body>
            {pending && (
              <>
                <p data-testid="impact-count">{impactSentence(pending.impact)}</p>
                {pending.impact.gained.length > 0 && (
                  <p className="mb-1" data-testid="impact-gained">
                    {`They gain: ${pending.impact.gained.join(', ')}.`}
                  </p>
                )}
                {pending.impact.lost.length > 0 && (
                  <p className="mb-1" data-testid="impact-lost">
                    {`They lose: ${pending.impact.lost.join(', ')}.`}
                  </p>
                )}
                <p className="mb-0 text-muted fs-small">
                  Anyone whose permissions were changed individually keeps those changes.
                </p>
              </>
            )}
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Cancel>Cancel</Dialog.Cancel>
            <Button
              data-testid="confirm-role-save"
              disabled={saving}
              onClick={() => pending && commit(pending.payload)}
            >
              Save Role
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </div>
  );
}
